// AI 어시스턴트 채팅 Zustand 스토어 — Supabase 영속화 + localStorage 폴백
// 진실 소스: Supabase `chat_sessions` + `chat_history` (RLS: store_id = auth.uid())
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from './authStore'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

export interface ChatSession {
  id: string
  title: string          // 첫 번째 사용자 메시지 요약
  messages: ChatMessage[]
  createdAt: string
  updatedAt: string
}

// 인증된 사용자 ID
function getStoreId(): string | null {
  return useAuthStore.getState().user?.id ?? null
}

// 임시 ID 생성
function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

function createNewSession(): ChatSession {
  return {
    id: genId(),
    title: '새 대화',
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

interface ChatState {
  sessions: ChatSession[]
  activeSessionId: string | null
  isPanelOpen: boolean
  isSynced: boolean
  loading: boolean

  // Supabase 동기화
  syncFromServer: () => Promise<void>

  // UI 액션
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void

  // 세션 관리
  newSession: () => string
  setActiveSession: (id: string) => void
  addMessage: (sessionId: string, msg: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  deleteSession: (id: string) => Promise<void>
  clearAll: () => Promise<void>
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      sessions: [],
      activeSessionId: null,
      isPanelOpen: false,
      isSynced: false,
      loading: false,

      // ─── 서버 → 클라이언트 동기화 ───
      syncFromServer: async () => {
        const storeId = getStoreId()
        if (!storeId) return

        set({ loading: true })
        try {
          // 세션 목록 가져오기
          const { data: sessionsData, error: sessErr } = await supabase
            .from('chat_sessions')
            .select('*')
            .eq('store_id', storeId)
            .order('updated_at', { ascending: false })
          if (sessErr) throw sessErr

          if (!sessionsData?.length) {
            set({ sessions: [], isSynced: true })
            return
          }

          // 세션별 메시지 가져오기
          const sessionIds = sessionsData.map((s: Record<string, unknown>) => s.id as string)
          const { data: messagesData, error: msgErr } = await supabase
            .from('chat_history')
            .select('*')
            .in('session_id', sessionIds)
            .order('created_at', { ascending: true })
          if (msgErr) throw msgErr

          // 세션별 메시지 그룹핑
          const msgBySession = new Map<string, ChatMessage[]>()
          for (const row of (messagesData ?? []) as Array<Record<string, unknown>>) {
            const sid = row.session_id as string
            if (!msgBySession.has(sid)) msgBySession.set(sid, [])
            msgBySession.get(sid)!.push({
              id: row.id as string,
              role: row.role as 'user' | 'assistant',
              content: row.message as string,
              timestamp: (row.created_at as string) ?? new Date().toISOString(),
            })
          }

          // 세션 조립
          const sessions: ChatSession[] = sessionsData.map((row: Record<string, unknown>) => ({
            id: row.id as string,
            title: (row.title as string) ?? '새 대화',
            messages: msgBySession.get(row.id as string) ?? [],
            createdAt: (row.created_at as string) ?? new Date().toISOString(),
            updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
          }))

          set({ sessions, isSynced: true })
        } catch (e) {
          console.error('[chatStore] syncFromServer 실패:', e)
        } finally {
          set({ loading: false })
        }
      },

      openPanel: () => set({ isPanelOpen: true }),
      closePanel: () => set({ isPanelOpen: false }),
      togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),

      // ─── 새 세션 생성 ───
      newSession: () => {
        const session = createNewSession()
        // optimistic UI
        set((s) => ({
          sessions: [session, ...s.sessions],
          activeSessionId: session.id,
        }))

        // 비동기로 서버에 생성 (await 불필요, fire-and-forget)
        const storeId = getStoreId()
        if (storeId) {
          void (async () => {
            try {
              const { data, error } = await supabase
                .from('chat_sessions')
                .insert({ store_id: storeId, title: session.title })
                .select()
                .single()
              if (error) throw error

              // 임시 ID → DB ID로 교체
              const dbId = data.id as string
              set((s) => ({
                sessions: s.sessions.map((sess) =>
                  sess.id === session.id ? { ...sess, id: dbId } : sess
                ),
                activeSessionId: s.activeSessionId === session.id ? dbId : s.activeSessionId,
              }))
            } catch (e) {
              console.error('[chatStore] newSession 서버 생성 실패:', e)
            }
          })()
        }

        return session.id
      },

      setActiveSession: (id) => set({ activeSessionId: id }),

      // ─── 메시지 추가 ───
      addMessage: (sessionId, msg) => {
        const newMsg: ChatMessage = {
          ...msg,
          id: genId(),
          timestamp: new Date().toISOString(),
        }

        // optimistic UI
        set((s) => ({
          sessions: s.sessions.map((sess) => {
            if (sess.id !== sessionId) return sess
            const messages = [...sess.messages, newMsg]
            const firstUser = messages.find((m) => m.role === 'user')
            const title = firstUser
              ? firstUser.content.slice(0, 30) + (firstUser.content.length > 30 ? '...' : '')
              : '새 대화'
            return { ...sess, messages, title, updatedAt: new Date().toISOString() }
          }),
        }))

        // 비동기 서버 저장
        const storeId = getStoreId()
        if (storeId) {
          void (async () => {
            try {
              // 세션 제목 업데이트 (첫 메시지 기반)
              const session = get().sessions.find((s) => s.id === sessionId)
              if (session) {
                const firstUser = session.messages.find((m) => m.role === 'user')
                if (firstUser) {
                  const title = firstUser.content.slice(0, 30) + (firstUser.content.length > 30 ? '...' : '')
                  void supabase
                    .from('chat_sessions')
                    .update({ title, updated_at: new Date().toISOString() })
                    .eq('id', sessionId)
                }
              }

              // 메시지 저장
              const { data, error } = await supabase
                .from('chat_history')
                .insert({
                  store_id: storeId,
                  session_id: sessionId,
                  role: msg.role,
                  message: msg.content,
                })
                .select()
                .single()
              if (error) throw error

              // 임시 ID → DB ID로 교체
              if (data) {
                const dbMsgId = data.id as string
                set((s) => ({
                  sessions: s.sessions.map((sess) => {
                    if (sess.id !== sessionId) return sess
                    return {
                      ...sess,
                      messages: sess.messages.map((m) =>
                        m.id === newMsg.id ? { ...m, id: dbMsgId } : m
                      ),
                    }
                  }),
                }))
              }
            } catch (e) {
              console.error('[chatStore] addMessage 서버 저장 실패:', e)
            }
          })()
        }
      },

      // ─── 세션 삭제 ───
      deleteSession: async (id) => {
        const prev = get().sessions
        set((s) => {
          const sessions = s.sessions.filter((sess) => sess.id !== id)
          return {
            sessions,
            activeSessionId: s.activeSessionId === id ? (sessions[0]?.id ?? null) : s.activeSessionId,
          }
        })

        const storeId = getStoreId()
        if (!storeId || id.includes('-') && !id.includes('--')) {
          // 임시 ID 패턴이면 서버 삭제 불필요
          return
        }

        try {
          // chat_history는 ON DELETE CASCADE로 자동 삭제
          const { error } = await supabase
            .from('chat_sessions')
            .delete()
            .eq('id', id)
          if (error) throw error
        } catch (e) {
          console.error('[chatStore] deleteSession 실패:', e)
          set({ sessions: prev })
        }
      },

      // ─── 전체 삭제 ───
      clearAll: async () => {
        const prev = get().sessions
        set({ sessions: [], activeSessionId: null })

        const storeId = getStoreId()
        if (!storeId) return

        try {
          const { error } = await supabase
            .from('chat_sessions')
            .delete()
            .eq('store_id', storeId)
          if (error) throw error
        } catch (e) {
          console.error('[chatStore] clearAll 실패:', e)
          set({ sessions: prev })
        }
      },
    }),
    { name: 'cafe-brain-chat' }
  )
)

// ─── 인증 상태 변화 자동 반응 ───
if (typeof window !== 'undefined') {
  let lastUserId: string | null = useAuthStore.getState().user?.id ?? null

  if (lastUserId) {
    void useChatStore.getState().syncFromServer()
  }

  useAuthStore.subscribe((state) => {
    const newId = state.user?.id ?? null
    if (newId === lastUserId) return

    if (newId) {
      void useChatStore.getState().syncFromServer()
    } else {
      // 로그아웃 — 채팅 데이터 클리어
      useChatStore.setState({
        sessions: [],
        activeSessionId: null,
        isSynced: false,
        loading: false,
      })
    }
    lastUserId = newId
  })
}
